/**
 * Ownly's own booking page: the pieces the browser and the server share.
 *
 * Replaces the Calendly embed on /book once `OWN_BOOKING_ENABLED` is "true" (off in production
 * until Tom approves the switch). The browser never talks to Lead Engine: it calls this site's
 * /api/booking/* routes, which add the shared secret, Turnstile, a rate limit and test mode.
 * Everything here is pure so it is tested without a browser.
 */

export const HOST_TIME_ZONE = "Europe/London";

export type CallType = "meet" | "phone";
/** "sales" is a prospect's call; "applicant" is a job applicant's chat (no sales wording at all). */
export type MeetingKind = "sales" | "applicant";

export type PublicBooking = {
  /** The booking's own id, for the Meta event id. Optional: older Lead Engine builds omit it. */
  reference?: string;
  status: string;
  startsAt: string;
  endsAt: string;
  timeZone: string;
  meetUrl: string | null;
  icsUid: string;
  firstName: string | null;
  dryRun: boolean;
  changeable: boolean;
  /** Optional: bookings made before meeting types existed were all a 30-minute review on Meet. */
  callType?: CallType;
  /** "ending 123" for a phone call. Never the whole number. */
  phoneHint?: string | null;
  minutes?: number;
  meetingName?: string;
  meetingType?: string;
  kind?: MeetingKind;
};

export type MeetingTypeInfo = { slug: string; name: string; minutes: number; callTypes: CallType[]; kind?: MeetingKind };

export type Availability = {
  timeZone: string;
  slotMinutes: number;
  days: { date: string; slots: string[] }[];
  dryRun: boolean;
  preview: boolean;
  meetingType?: MeetingTypeInfo;
  /** Only on a person's own page (/book/tom). */
  host?: { slug: string; name: string } | null;
  /** Whether the WhatsApp reminder box can be offered. */
  whatsappReminders?: boolean;
};

/** "review", "intro call", "chat": what a booking is called in a sentence. */
export function bookingNoun(b: { meetingName?: string; kind?: MeetingKind } | null | undefined): string {
  if (b?.kind === "applicant") return "chat";
  const stripped = (b?.meetingName ?? "")
    .replace(/^\s*\d+\s*-?\s*(minute|min|hour)s?\s*/i, "")
    .trim()
    .toLowerCase();
  return stripped || "review";
}

/** Where to book again after a cancellation: applicants back to their own page. */
export function bookAgainHref(b: { kind?: MeetingKind } | null | undefined): string {
  return b?.kind === "applicant" ? "/book/applicant" : "/book";
}

/** "Thirty minutes", "Fifteen minutes", or "45 minutes". */
export function minutesInWords(minutes: number): string {
  const words: Record<number, string> = { 15: "Fifteen", 20: "Twenty", 30: "Thirty", 45: "Forty-five", 60: "Sixty", 90: "Ninety" };
  return `${words[minutes] ?? minutes} minutes`;
}

const UK_PHONE = /^\+44[1-9]\d{8,9}$/;
const UK_MOBILE = /^\+447[1-9]\d{8}$/;

/**
 * "+447700900123" from the ways people type a UK number, or null. Mirrors Lead Engine's check
 * (lib/booking/phone.ts) so the form can say what is wrong before anything is sent.
 */
export function ukPhone(raw: string | null | undefined): string | null {
  if (!raw || raw.length > 40) return null;
  let s = raw.trim();
  if (s.startsWith("+")) s = `+${s.slice(1).replace(/\(0\)/g, "").replace(/\D/g, "")}`;
  else if (s.startsWith("00")) s = `+${s.slice(2).replace(/\D/g, "")}`;
  else {
    const digits = s.replace(/\D/g, "");
    if (digits.startsWith("0")) s = `+44${digits.replace(/^0+/, "")}`;
    else if (digits.startsWith("44")) s = `+${digits}`;
    else return null;
  }
  return UK_PHONE.test(s) ? s : null;
}

/** "07700 900123" from "+447700900123", for people to read. Anything else comes back unchanged. */
export function displayUkPhone(value: string | null | undefined): string {
  const e164 = ukPhone(value);
  if (!e164) return value?.trim() ?? "";
  const national = `0${e164.slice(3)}`;
  if (national.startsWith("02")) return `${national.slice(0, 3)} ${national.slice(3, 7)} ${national.slice(7)}`;
  return national.length === 11 ? `${national.slice(0, 5)} ${national.slice(5)}` : national;
}

export function isUkMobile(e164: string | null | undefined): boolean {
  return Boolean(e164 && UK_MOBILE.test(e164));
}

/** Only these characters ever go into a URL path or a request: lower case, digits, dashes. */
export const BOOKING_SLUG = /^[a-z][a-z0-9-]{1,29}$/;

/** Every reason a booking call can fail, as the browser is told it. */
export type BookingFailure =
  | "unavailable"
  | "slot_taken"
  | "slot_unavailable"
  | "too_many"
  | "not_found"
  | "not_changeable"
  | "phone_invalid"
  | "security_check"
  | "rate_limited"
  | "invalid"
  | "network"
  /** A person's own page whose diary is not connected yet, or who is not taking bookings. */
  | "host_not_ready";

/**
 * What a person reads for each failure. Plain, kind, and always with a way forward. Never a code.
 */
export function failureMessage(reason: BookingFailure, contactEmail: string): string {
  switch (reason) {
    case "slot_taken":
      return "Someone has just taken that time. Here are the times still free.";
    case "slot_unavailable":
      return "That time is no longer available. Please pick another.";
    case "too_many":
      return `You already have two calls booked with us. To change one, use the link in its email, or email ${contactEmail}.`;
    case "phone_invalid":
      return "Please give a UK phone number we can ring, like 07700 900123.";
    case "not_found":
      return `We could not find that booking. The link may be from an older email. Email ${contactEmail} and we will sort it.`;
    case "not_changeable":
      return `This booking can no longer be changed online. Email ${contactEmail} and we will help.`;
    case "security_check":
      return "The security check did not go through. Please try again.";
    case "rate_limited":
      return "That was a lot of tries in a short time. Please wait a minute and try again.";
    case "invalid":
      return "Something in the form was not quite right. Please check your details and try again.";
    case "network":
      return "We could not reach our booking system. Check your connection and try again.";
    case "host_not_ready":
      return "This person is not taking bookings online just yet. You can still book a call with the team.";
    case "unavailable":
    default:
      return `You can still book through our backup calendar, or email ${contactEmail} and we will find a time.`;
  }
}

/** The visitor's own time zone, or London when the browser will not say. */
export function visitorTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || HOST_TIME_ZONE;
  } catch {
    return HOST_TIME_ZONE;
  }
}

export function isValidTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** "New York" from "America/New_York". */
export function zoneCity(zone: string): string {
  if (zone === HOST_TIME_ZONE) return "UK";
  const last = zone.split("/").pop() ?? zone;
  return last.replace(/_/g, " ");
}

/** "UK time", "New York time": how the zone is named in "Times shown in ...". */
export function zoneLabel(zone: string): string {
  return `${zoneCity(zone)} time`;
}

/** Where a visitor's "show in UK time" or "show in my time" choice is kept, per browser. */
export const DISPLAY_ZONE_STORAGE_KEY = "ownly-booking-time-zone";
export type DisplayZonePreference = "uk" | "local";

export function parseDisplayZonePreference(value: unknown): DisplayZonePreference | null {
  return value === "uk" || value === "local" ? value : null;
}

/** The zone to show times in: UK time if they asked for it, otherwise their browser's own. */
export function displayZoneFor(preference: DisplayZonePreference | null, browserZone: string): string {
  return preference === "uk" ? HOST_TIME_ZONE : browserZone;
}

/** Whether two zones show the same clock right now and for the coming week. */
export function sameClock(a: string, b: string, at: Date = new Date()): boolean {
  if (a === b) return true;
  const fmt = (zone: string, when: Date) =>
    new Intl.DateTimeFormat("en-GB", { timeZone: zone, hour: "2-digit", minute: "2-digit", day: "2-digit", hourCycle: "h23" }).format(when);
  const week = Array.from({ length: 8 }, (_, i) => new Date(at.getTime() + i * 24 * 3600_000));
  return week.every((when) => fmt(a, when) === fmt(b, when));
}

/** "10:00am", or "10am" never: always with minutes, so a column of times lines up. */
export function formatTime(iso: string, zone: string): string {
  return new Intl.DateTimeFormat("en-GB", { timeZone: zone, hour: "numeric", minute: "2-digit", hour12: true })
    .format(new Date(iso))
    .replace(/\s/g, "")
    .toLowerCase();
}

/** "Tuesday 13 October". */
export function formatLongDay(iso: string, zone: string): string {
  return new Intl.DateTimeFormat("en-GB", { timeZone: zone, weekday: "long", day: "numeric", month: "long" }).format(new Date(iso));
}

/** The short zone name, "BST", "GMT", "EDT", for the line under a confirmed time. */
export function zoneAbbreviation(iso: string, zone: string): string {
  return (
    new Intl.DateTimeFormat("en-GB", { timeZone: zone, timeZoneName: "short" })
      .formatToParts(new Date(iso))
      .find((p) => p.type === "timeZoneName")?.value ?? ""
  );
}

export type DisplayDay = {
  /** YYYY-MM-DD in the zone the visitor is reading in. */
  key: string;
  weekday: string;
  dayOfMonth: string;
  month: string;
  slots: string[];
};

function dayKey(iso: string, zone: string): string {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(iso));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/**
 * Slots regrouped by the date the VISITOR sees. Lead Engine groups by London's date; somebody in
 * Sydney reading 6:30pm London on a Tuesday sees 4:30am on Wednesday, and it has to sit under
 * Wednesday or the page contradicts itself.
 */
export function groupSlotsByDay(slots: string[], zone: string): DisplayDay[] {
  const byDay = new Map<string, string[]>();
  for (const slot of [...slots].sort()) {
    const key = dayKey(slot, zone);
    byDay.set(key, [...(byDay.get(key) ?? []), slot]);
  }
  return [...byDay.entries()].map(([key, list]) => {
    const sample = new Date(list[0]);
    const part = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat("en-GB", { timeZone: zone, ...opts }).format(sample);
    return { key, weekday: part({ weekday: "short" }), dayOfMonth: part({ day: "numeric" }), month: part({ month: "short" }), slots: list };
  });
}

/**
 * Lead Engine's `days` (grouped by the HOST's London date) flattened and regrouped by the date in
 * the zone the visitor is reading in. A slot listed twice is shown once.
 */
export function regroupDays(days: { date?: string; slots: string[] }[], zone: string): DisplayDay[] {
  return groupSlotsByDay([...new Set(days.flatMap((d) => d.slots))], zone);
}

/** Morning, afternoon and evening, in the reader's zone, so a long list scans. */
export function splitByPartOfDay(slots: string[], zone: string): { label: string; slots: string[] }[] {
  const hour = (iso: string) => Number(new Intl.DateTimeFormat("en-GB", { timeZone: zone, hour: "2-digit", hourCycle: "h23" }).format(new Date(iso)));
  const groups = [
    { label: "Morning", slots: slots.filter((s) => hour(s) < 12) },
    { label: "Afternoon", slots: slots.filter((s) => hour(s) >= 12 && hour(s) < 17) },
    { label: "Evening", slots: slots.filter((s) => hour(s) >= 17) },
  ];
  return groups.filter((g) => g.slots.length > 0);
}

/** "30-minute review with Ownly", or whatever the booking was made as. */
function summaryOf(b: Pick<PublicBooking, "meetingName">): string {
  return `${b.meetingName ?? "30-minute review"} with Ownly`;
}

function detailsOf(b: Pick<PublicBooking, "meetUrl" | "callType" | "phoneHint">): string {
  if (b.callType === "phone") return `Ownly will call you on your number${b.phoneHint ? ` ${b.phoneHint}` : ""}.`;
  return b.meetUrl ? `Join on Google Meet: ${b.meetUrl}` : "The Google Meet link is in your calendar invite.";
}

function calendarStamp(iso: string): string {
  return new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

/** Google Calendar's "add event" template, for people whose invite has not landed yet. */
export function googleCalendarUrl(booking: Pick<PublicBooking, "startsAt" | "endsAt" | "meetUrl" | "callType" | "phoneHint" | "meetingName">): string {
  const url = new URL("https://calendar.google.com/calendar/render");
  url.searchParams.set("action", "TEMPLATE");
  url.searchParams.set("text", summaryOf(booking));
  url.searchParams.set("dates", `${calendarStamp(booking.startsAt)}/${calendarStamp(booking.endsAt)}`);
  url.searchParams.set("details", detailsOf(booking));
  if (booking.meetUrl) url.searchParams.set("location", booking.meetUrl);
  return url.toString();
}

function escapeIcs(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/\r?\n/g, "\\n").replace(/([,;])/g, "\\$1");
}

/**
 * The same .ics the confirmation email carries, for Apple Calendar and Outlook. Same UID as the
 * Google invite, so opening both leaves one entry.
 */
export function bookingIcs(
  booking: Pick<PublicBooking, "startsAt" | "endsAt" | "meetUrl" | "icsUid" | "callType" | "phoneHint" | "meetingName">,
  now = new Date(),
): string {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Ownly Digital//Booking//EN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${escapeIcs(booking.icsUid)}`,
    `DTSTAMP:${calendarStamp(now.toISOString())}`,
    `DTSTART:${calendarStamp(booking.startsAt)}`,
    `DTEND:${calendarStamp(booking.endsAt)}`,
    `SUMMARY:${escapeIcs(summaryOf(booking))}`,
    `DESCRIPTION:${escapeIcs(detailsOf(booking))}`,
    ...(booking.meetUrl ? [`LOCATION:${escapeIcs(booking.meetUrl)}`] : []),
    "STATUS:CONFIRMED",
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return `${lines.join("\r\n")}\r\n`;
}

/** What the browser posts to /api/booking/book. Mirrors the server's checks. */
export const BOOKING_FIELD_LIMITS = { name: 120, email: 254, phone: 40, note: 500, answers: 500 } as const;

export const MANAGE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/** Read once on the manage page, then removed from the address bar (see ManageBooking). */
export const MANAGE_PATH = "/book/manage";
